// Parking Selection card › Select Parking (M5-F5; rows M5.6–M5.9 / M5.21, DC-25 / DC-46 / DC-97 / DC-126 / DC-176).
// Today's "Parking Selection Flow" pop-up (ParkingModal.jsx, prototype pkDraw) opened by the CRM for a CRM 2.0
// booking: Included / Upgradable counts, Option 1 Included Parking and Option 2 Upgradable Parking by floor,
// Pricing Summary with "I have reviewed the pricing summary" for an upgrade, Skip / Reset / Confirm (Confirm Upgrade).
// Options, prices and the save are on the server (GET /crm2/parking/options, POST /crm2/parking/select | skip):
// slots are locked so one slot never goes to two bookings; a slot just taken refreshes the list.
import React, { useEffect, useMemo, useState } from "react";
import { Modal } from "@mui/material";
import Close from "../../Icons/Close";
import { crm2ParkingOptions, crm2ParkingSelect, crm2ParkingSkip } from "../../../Services/Crm2List";
import { dismissLoader, presentLoader } from "../../../Services/loader";
import { errorTost, successTost } from "../../../Services/Toast";
import ConfirmationAlert from "../../../Subcomponents/ConfirmationAlert";
import { unitLine } from "../ui/cardParts";

const R = { fontFamily: "Roboto, sans-serif" };
const rs = (n) => `₹${Math.round(Number(n) || 0).toLocaleString("en-IN")}`;
const outBtn = { flex: 1, fontSize: "1.6rem", fontWeight: 600, padding: "8px 16px", borderRadius: 12, border: "1px solid rgba(25,118,210,.5)", color: "#1976d2", background: "transparent", cursor: "pointer", textTransform: "uppercase" };

function Slot({ s, kind, on, onPick }) {
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onPick}
      onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onPick()}
      style={{ cursor: "pointer", border: `2px solid ${on ? "#1976d2" : "#ddd"}`, borderRadius: 12, boxShadow: "0 4px 10px rgba(0,0,0,.1)", background: kind === "up" && on ? "#bbdefb" : "#f5f5f5", padding: 16, transition: "all .3s ease" }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
        <span style={{ fontSize: "2rem", fontWeight: 600, color: "#333" }}>#{s.number}</span>
        {on && (
          <span style={{ color: "#1976d2", fontSize: "2.2rem" }} aria-label="Selected">
            ✔
          </span>
        )}
      </div>
      <span style={{ display: "inline-block", marginTop: 16, fontSize: "1.4rem", fontWeight: 700, padding: "4px 12px", borderRadius: 16, background: "#1976d2", color: "#fff" }}>{s.type}</span>
      <p style={{ marginTop: 16, fontSize: "1.4rem", fontWeight: 500, color: "#555" }}>Floor: {s.floor}</p>
      <p style={{ marginTop: 16, fontSize: "1.6rem", fontWeight: 600, color: "rgba(0,0,0,.6)" }}>{kind === "inc" ? "Included" : rs(s.price)}</p>
    </div>
  );
}

function Section({ title, list, kind, picked, onPick }) {
  const floors = [...new Set(list.map((s) => s.floor))];
  return (
    <>
      <p style={{ fontSize: "2rem", fontWeight: 700, margin: "8px 0" }}>{title}</p>
      {kind === "up" && <hr style={{ border: 0, borderTop: "1px solid #e0e0e0", margin: "0 0 16px" }} />}
      {!list.length && <p style={{ fontSize: "1.4rem", color: "#667085", margin: "0 0 24px" }}>No {kind === "inc" ? "included" : "upgradable"} parking available.</p>}
      {floors.map((f) => (
        <div key={f} style={{ marginBottom: 32 }}>
          {kind === "inc" && <hr style={{ border: 0, borderTop: "1px solid #e0e0e0", margin: "0 0 16px" }} />}
          <p style={{ fontSize: "2rem", fontWeight: 700, color: "#1976d2", marginBottom: 8 }}>{f}</p>
          <hr style={{ border: 0, borderTop: "1px solid #e0e0e0", margin: "0 0 16px" }} />
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(20rem,1fr))", gap: 24 }}>
            {list
              .filter((s) => s.floor === f)
              .map((s) => (
                <Slot key={s.id} s={s} kind={kind} on={picked.includes(s.id)} onPick={() => onPick(s)} />
              ))}
          </div>
        </div>
      ))}
    </>
  );
}

export default function Crm2ParkingModal({ card, onClose, onDone }) {
  const apId = card.allotmentPaymentId;
  const [o, setO] = useState(null);
  const [inc, setInc] = useState([]);
  const [up, setUp] = useState([]);
  const [ok, setOk] = useState(false);
  const [sum, setSum] = useState(null);
  const [ask, setAsk] = useState(null); // "confirm" | "skip"

  const load = () =>
    crm2ParkingOptions(apId)
      .then((d) => {
        console.log("[crm2][Crm2ParkingModal] options", { allotmentPaymentId: apId, flow: d?.flow, applicable: d?.applicable, included: d?.included?.length, upgrades: d?.upgrades?.length });
        // a re-pick shows the slots this booking already holds
        const mineInc = (d.current || []).filter((s) => !s.isUpgrade);
        const mineUp = (d.current || []).filter((s) => s.isUpgrade);
        setO({ ...d, included: [...mineInc, ...(d.included || []).filter((s) => !mineInc.some((m) => m.id === s.id))], upgrades: [...mineUp, ...(d.upgrades || []).filter((s) => !mineUp.some((m) => m.id === s.id))] });
        setInc(mineInc.map((s) => s.id));
        setUp(mineUp.map((s) => s.id));
      })
      .catch((e) => {
        errorTost(e?.response?.data?.message || "Could not load the parking. Please try again.");
        onClose();
      });
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apId]);

  const maxInc = o?.rules?.maxIncluded || 0;
  const maxUp = o?.rules?.maxUpgrade || 0;
  const pick = (kind) => (s) => {
    setOk(false);
    const [list, set, max] = kind === "inc" ? [inc, setInc, maxInc] : [up, setUp, maxUp];
    if (list.includes(s.id)) return set(list.filter((x) => x !== s.id));
    // today's rule: the unit's parking count is the total; an upgrade takes the place of an included parking.
    // When the total is full, the new pick replaces one of the other kind (or the earlier pick of the same kind).
    const other = kind === "inc" ? [up, setUp] : [inc, setInc];
    if (list.length >= max) return set([...list.slice(1), s.id]);
    if (inc.length + up.length >= maxInc) {
      if (other[0].length) other[1](other[0].slice(1));
      else return set([...list.slice(1), s.id]);
    }
    set([...list, s.id]);
  };
  const upSlots = useMemo(() => (o?.upgrades || []).filter((s) => up.includes(s.id)), [o, up]);
  const upCost = upSlots.reduce((a, s) => a + (Number(s.price) || 0), 0);

  // Pricing Summary from the server (nothing saved)
  useEffect(() => {
    setSum(null);
    if (!upSlots.length) return;
    crm2ParkingSelect(apId, { includedIds: inc, upgradeIds: up, preview: true })
      .then(setSum)
      .catch((e) => errorTost(e?.response?.data?.message || "Could not work out the price"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apId, up.join(","), inc.join(",")]);

  const any = inc.length + up.length > 0;
  const can = any && (!upSlots.length || (ok && !!sum));

  const save = async (what) => {
    setAsk(null);
    presentLoader();
    try {
      const r = what === "skip" ? await crm2ParkingSkip(apId) : await crm2ParkingSelect(apId, { includedIds: inc, upgradeIds: up });
      console.log("[crm2][Crm2ParkingModal] saved", { allotmentPaymentId: apId, what, status: r?.status });
      successTost(what === "skip" ? "Parking skipped. Moved to Final Review" : "Parking confirmed. Moved to Final Review");
      onDone();
    } catch (e) {
      const message = e?.response?.data?.message || "Parking not saved. Please try again.";
      console.error("[crm2][Crm2ParkingModal] save failed", { allotmentPaymentId: apId, what, status: e?.response?.status, code: e?.response?.data?.code, message });
      errorTost(message);
      if (e?.response?.data?.code === "SLOT_TAKEN") load();
    } finally {
      dismissLoader();
    }
  };

  const line = (l, v, big) => (
    <div style={{ display: "flex", justifyContent: "space-between", marginTop: 8 }}>
      <span style={{ fontSize: big ? "1.7rem" : l === "Agreement Value" ? "1.5rem" : "1.2rem", fontWeight: big ? 600 : 100 }}>{l}</span>
      <span style={{ fontSize: big ? "1.8rem" : l === "Agreement Value" ? "1.5rem" : "1.2rem", fontWeight: big ? 800 : 100, color: big ? "#1976d2" : undefined }}>{rs(v)}</span>
    </div>
  );

  return (
    <>
      <Modal open onClose={onClose}>
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Parking Selection Flow"
          style={{ ...R, position: "absolute", top: "50%", left: "50%", transform: "translate(-50%,-50%)", width: "min(1200px,94vw)", maxHeight: "92vh", display: "flex", flexDirection: "column", background: "#f4f4f9", borderRadius: 12, boxShadow: "0 10px 20px rgba(0,0,0,.1)", outline: "none" }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 24px" }}>
            <h2 style={{ ...R, margin: 0, fontSize: "2rem", fontWeight: 600 }}>Parking Selection Flow</h2>
            <span role="button" tabIndex={0} aria-label="Close" style={{ cursor: "pointer" }} onClick={onClose} onKeyDown={(e) => e.key === "Enter" && onClose()}>
              <Close />
            </span>
          </div>
          <div style={{ padding: "16px 24px", borderTop: "1px solid #e0e0e0", borderBottom: "1px solid #e0e0e0", overflow: "auto" }}>
            <p style={{ fontSize: "1.3rem", color: "#667085", margin: "0 0 12px" }}>
              {unitLine(card)} · {card.leadName} · {card.displayUnitType}
            </p>
            {!o ? (
              <p style={{ fontSize: "1.6rem", textAlign: "center", padding: "4rem 0", color: "#667085" }}>Loading…</p>
            ) : (
              <>
                <div style={{ display: "flex", gap: 24, marginBottom: 24 }}>
                  <div style={{ padding: 16, border: "1px solid #e0e0e0", borderRadius: 8, background: "#e3f2fd", width: "100%", textAlign: "center" }}>
                    <p style={{ fontSize: "1.8rem", fontWeight: 700, color: "#0d47a1" }}>Included Parking</p>
                    <p style={{ fontSize: "2.6rem", fontWeight: 800, color: "#0d47a1", marginTop: 8 }}>{maxInc}</p>
                  </div>
                  <div style={{ padding: 16, border: "1px solid #e0e0e0", borderRadius: 8, background: "#fff3e0", width: "100%", textAlign: "center" }}>
                    <p style={{ fontSize: "1.8rem", fontWeight: 700, color: "#e65100" }}>Upgradable Parking</p>
                    <p style={{ fontSize: "2.6rem", fontWeight: 800, color: "#e65100", marginTop: 8 }}>{maxUp}</p>
                  </div>
                </div>
                <Section title="Option 1: Included Parking" list={o.included || []} kind="inc" picked={inc} onPick={pick("inc")} />
                {maxUp > 0 && <Section title="Option 2: Upgradable Parking" list={o.upgrades || []} kind="up" picked={up} onPick={pick("up")} />}
                {upSlots.length > 0 && (
                  <>
                    <div style={{ margin: "24px 0", padding: 32, border: "2px solid #bbb", borderRadius: 12 }}>
                      <p style={{ fontSize: "2.4rem", fontWeight: 700, marginBottom: 16, textAlign: "center" }}>Pricing Summary</p>
                      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 8 }}>
                        <span style={{ fontSize: "1.7rem", fontWeight: 600 }}>Upgraded Parking cost</span>
                        <span style={{ fontSize: "1.7rem", fontWeight: 700 }}>{rs(upCost)}</span>
                      </div>
                      <hr style={{ border: 0, borderTop: "1px solid #e0e0e0", margin: "16px 0" }} />
                      {sum ? (
                        <>
                          {line("Agreement Value", sum.unitCost.bsp)}
                          {line("New SDR", sum.unitCost.sdr)}
                          {line("New GST", sum.unitCost.gst)}
                          {line("New Total Unit Cost", sum.unitCost.total, true)}
                        </>
                      ) : (
                        <p style={{ fontSize: "1.3rem", color: "#667085" }}>Working out the price…</p>
                      )}
                      <p style={{ fontSize: "1.2rem", fontWeight: 100, margin: "8px 0 0 50%" }}>(Total is calculated as: Agreement Value + New SDR + New GST)</p>
                    </div>
                    <label style={{ display: "flex", alignItems: "center", gap: 16, marginTop: 16, cursor: "pointer" }}>
                      <input type="checkbox" checked={ok} onChange={(e) => setOk(e.target.checked)} style={{ width: 20, height: 20 }} />
                      <span style={{ fontSize: "1.6rem", fontWeight: 600 }}>I have reviewed the pricing summary.</span>
                    </label>
                  </>
                )}
              </>
            )}
          </div>
          {o && !any && (
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 16, padding: "16px 32px" }}>
              <button type="button" style={{ ...outBtn, flex: "none", padding: "8px 24px" }} onClick={() => setAsk("skip")}>
                Skip
              </button>
            </div>
          )}
          {o && any && (
            <div style={{ display: "flex", gap: 16, padding: "24px 32px" }}>
              <button type="button" style={outBtn} onClick={() => setAsk("skip")}>
                Skip
              </button>
              <button
                type="button"
                style={outBtn}
                onClick={() => {
                  setInc([]);
                  setUp([]);
                  setOk(false);
                }}
              >
                Reset
              </button>
              <button
                type="button"
                disabled={!can}
                onClick={() => setAsk("confirm")}
                style={{ ...outBtn, border: 0, color: can ? "#fff" : "rgba(0,0,0,.26)", background: can ? "#1976d2" : "rgba(0,0,0,.12)", cursor: can ? "pointer" : "default" }}
              >
                {upSlots.length ? "Confirm Upgrade" : "Confirm"}
              </button>
            </div>
          )}
        </div>
      </Modal>
      {ask && (
        <ConfirmationAlert
          open
          close={() => setAsk(null)}
          buttonTitle={"Cancel"}
          title={"Warning"}
          subTitle={ask === "skip" ? `No parking for ${card.leadName || "this booking"}? The customer sees "No parking for this unit" and the stamp duty & charges payment opens.` : `Parking is selected for ${card.leadName || "this booking"}, do you want to proceed?`}
          returnRespButton={"Confirm"}
          confirm={(v) => (v === "confirmed" ? save(ask) : setAsk(null))}
        />
      )}
    </>
  );
}
